"use client";

import { useState, useEffect, useRef } from "react";
import {
  Grid,
  Card,
  Container,
  Typography,
  Box,
  Chip,
  Avatar,
  Button,
  Paper,
  LinearProgress,
  Rating,
  CircularProgress,
  Skeleton,
  Stack, // Import Stack
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles"; // Import alpha and useTheme
import {
  LocationOn,
  Print,
  Image,
  PictureAsPdf,
  FactCheck, // Import FactCheck
  CheckCircle, // Import CheckCircle
  ArrowForward as ArrowForwardIcon, // Import ArrowForward
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";
import apiClient from "../../context/apiClient";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom"; // Import useNavigate

const transformApiData = (apiData) => {
  let location = "Location not specified";
  try {
    const parsedLocation = JSON.parse(apiData.location);
    if (Array.isArray(parsedLocation) && parsedLocation.length > 0) {
      location = parsedLocation.join(", ");
    }
  } catch (e) {
    if (
      typeof apiData.location === "string" &&
      apiData.location.trim() !== "" &&
      apiData.location.trim() !== "[]"
    ) {
      location = apiData.location;
    }
  }

  return {
    personalInfo: {
      name: apiData.talent_name || "N/A",
      title: apiData.role || "Role not specified",
      location: location,
      photo:
        apiData.profile_photo_url ||
        `https://i.pravatar.cc/150?u=${apiData.talent_name}`,
    },
    aiSpecializations: Object.entries(apiData.specialization_scores || {}).map(
      ([name, score]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        level: score || 0,
      }),
    ),
    frameworks: apiData.frameworks_tools || [],
    certifications: (apiData.rated_certifications || []).map((cert) => ({
      name: cert.name,
      issuer: cert.issued_by || "Verified Platform",
    })),
    globalReadiness: {
      relocationScore: apiData.relocation_score || 0,
      culturalAdaptability: apiData.cultural_adaptability_score || 0,
      communicationSkills: apiData.communication_skills_score || 0,
      technicalReadiness: apiData.technical_readiness_score || 0,
      overallScore: apiData.global_readiness_score || 0,
    },
    aiGeneratedInsights: {
      summary: apiData.ai_powered_summary || "No summary available.",
      strengths: apiData.key_strengths || [],
    },
  };
};

const RadialProgress = ({ value }) => {
  const theme = useTheme();
  const size = 120;
  const strokeWidth = 10;
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={size}
      height={size}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke={alpha(theme.palette.primary.main, 0.1)}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke={theme.palette.primary.main}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: "stroke-dashoffset 1s ease-in-out",
          }}
        />
      </svg>
      <Box position="absolute" textAlign="center">
        <Typography variant="h5" fontWeight="bold" color="primary">
          {value}%
        </Typography>
      </Box>
    </Box>
  );
};

// --- SKELETON COMPONENT ---
const SkillsPassportSkeleton = () => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const pageWidth = 794;
        const availableWidth = container.clientWidth - 48;
        const maxScale = 2.0;
        const minScale = 0.3;

        let newScale = Math.min(availableWidth / pageWidth, maxScale);
        newScale = Math.max(newScale, minScale);
        setScale(newScale);
      }
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: "1 1 auto",
          overflowX: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            transform: `scale(${scale})`,
            transformOrigin: "center top",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <Box
            sx={{
              width: "794px",
              height: "1123px",
              bgcolor: "background.paper",
              borderRadius: "24px",
              boxShadow: (theme) =>
                `0 24px 64px ${alpha(theme.palette.common.black, 0.15)}`,
              p: 3,
              display: "flex",
              flexDirection: "column",
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              position: "relative",
            }}
          >
            {/* Header Skeleton */}
            <Card
              elevation={0}
              sx={{
                height: 140,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                borderRadius: "20px",
                mb: 2,
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 3,
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Skeleton
                    variant="circular"
                    width={100}
                    height={100}
                    sx={{
                      border: (theme) =>
                        `3px solid ${theme.palette.background.paper}`,
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <Skeleton variant="text" sx={{ fontSize: "2.5rem" }} />
                  <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} />
                  <Skeleton variant="text" width="60%" />
                </Grid>
              </Grid>
            </Card>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                gap: 2,
                minHeight: 0,
              }}
            >
              {/* Left Column Skeleton */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ fontSize: "1.25rem", margin: "0 auto 12px auto" }}
                  />
                  <Skeleton
                    variant="circular"
                    width={120}
                    height={120}
                    sx={{ margin: "0 auto" }}
                  />
                  <Box mt={2}>
                    {[...Array(4)].map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="text"
                        width="80%"
                        sx={{ margin: "4px auto 0 auto" }}
                      />
                    ))}
                  </Box>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ fontSize: "1.25rem", mb: 1.5 }}
                  />
                  {[...Array(4)].map((_, i) => (
                    <Box key={i} mb={1.5}>
                      <Skeleton variant="text" />
                      <Skeleton
                        variant="rectangular"
                        height={8}
                        sx={{ borderRadius: 5 }}
                      />
                    </Box>
                  ))}
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ fontSize: "1.25rem", mb: 1.5 }}
                  />
                  <Grid container spacing={1}>
                    {[...Array(3)].map((_, i) => (
                      <Grid item xs={12} key={i}>
                        <Skeleton
                          variant="rectangular"
                          height={80}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>

              {/* Right Column Skeleton */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ fontSize: "1.25rem", mb: 1.5 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={100}
                    sx={{ my: 1 }}
                  />
                  <Skeleton variant="text" width="40%" sx={{ mt: 2, mb: 1 }} />
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={24}
                      sx={{ borderRadius: "16px" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={100}
                      height={24}
                      sx={{ borderRadius: "16px" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={90}
                      height={24}
                      sx={{ borderRadius: "16px" }}
                    />
                  </Box>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ fontSize: "1.25rem", mb: 1.5 }}
                  />
                  {[...Array(4)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={60}
                      sx={{ borderRadius: 2, mb: 1.5 }}
                    />
                  ))}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Paper square elevation={3} sx={{ flexShrink: 0, py: 2, px: 3 }}>
        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
          <Button variant="contained" disabled sx={{ minWidth: 140 }}>
            Download JPEG
          </Button>
          <Button variant="outlined" disabled sx={{ minWidth: 140 }}>
            Download PDF
          </Button>
          <Button variant="outlined" disabled sx={{ minWidth: 100 }}>
            Print
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const SkillsPassport = () => {
  const theme = useTheme();
  const notificationContext = useNotification();
  const showNotification = notificationContext
    ? notificationContext.showNotification
    : (message, severity) => console.log(`${severity}: ${message}`);

  const navigate = useNavigate(); // Hook for navigation
  const [passportData, setPassportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [scale, setScale] = useState(1);
  const [isProfileImageLoaded, setIsProfileImageLoaded] = useState(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  const passportRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchPassportData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get("/skills-passport/");
        if (response.data) {
          setPassportData(transformApiData(response.data));
        } else {
          throw new Error("No data received from API.");
        }
        setError(null);
      } catch (err) {
        console.error("Failed to fetch skills passport:", err);
        // Check for the specific error message from the API response
        const errorMessage =
          // --- THIS IS THE MODIFIED LINE ---
          err.response?.data?.error || // Check for the 'error' key
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message;

        if (
          errorMessage &&
          errorMessage.includes("No completed Skills Passport found")
        ) {
          setError(
            "No completed Skills Passport found. Please complete a mock interview and generate one.",
          );
        } else {
          setError(
            "Could not load the skills passport. Please try again later.",
          );
        }
      } finally {
        // Add a small delay to better showcase the skeleton
        setTimeout(() => setLoading(false), 1000);
      }
    };
    fetchPassportData();
  }, []);

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const pageWidth = 794;
        const availableWidth = container.clientWidth - 48; // Reduced padding
        const maxScale = 2.0; // Increased maxScale
        const minScale = 0.3; 

        let newScale = Math.min(availableWidth / pageWidth, maxScale);
        newScale = Math.max(newScale, minScale);
        setScale(newScale);
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [loading]);

  const handleDownload = async (format) => {
    if (!passportRef.current || isGenerating) {
      return;
    }

    if (!allImagesLoaded) {
      showNotification(
        "Please wait for all images to load before downloading.",
        "warning",
      );
      return;
    }

    setIsGenerating(true);
    showNotification(
      `Generating your passport ${format.toUpperCase()}...`,
      "info",
    );

    try {
      const canvas = await html2canvas(passportRef.current, {
        scale: 2,
        backgroundColor: theme.palette.background.paper,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false,
        logging: false,
        width: 794,
        height: 1123,
        scrollX: 0,
        scrollY: 0,
      });

      if (format === "pdf") {
        const imgData = canvas.toDataURL("image/png", 1.0);
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [794, 1123],
        });

        const imgWidth = 794;
        const imgHeight = 1123;
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        pdf.save("skills-passport.pdf");
      } else {
        const image = canvas.toDataURL(`image/${format}`, 1.0);
        const link = document.createElement("a");
        link.href = image;
        link.download = `skills-passport.${format}`;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      showNotification(
        `Passport ${format.toUpperCase()} downloaded successfully!`,
        "success",
      );
    } catch (err) {
      console.error(`Error generating ${format}:`, err);
      showNotification(
        `Failed to generate passport ${format.toUpperCase()}. Please try again.`,
        "error",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageLoad = () => {
    setIsProfileImageLoaded(true);
    // Set a small delay to ensure all images are fully rendered
    setTimeout(() => {
      setAllImagesLoaded(true);
    }, 500);
  };

  if (loading) {
    return (
      <Layout title="Skills Passport">
        <SkillsPassportSkeleton />
      </Layout>
    );
  }

  if (error) {
    // Specific, user-friendly message for when a passport doesn't exist yet
    if (
      error ===
      "No completed Skills Passport found. Please complete a mock interview and generate one."
    ) {
      return (
        <Layout title="Skills Passport">
          <Container maxWidth="md">
            <Card
              sx={{
                mt: 8,
                p: { xs: 3, sm: 4, md: 5 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Stack spacing={4} alignItems="center" sx={{ maxWidth: "650px" }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <FactCheck sx={{ fontSize: "48px", color: "primary.main" }} />
                </Box>

                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight="bold"
                  textAlign="center"
                  color="text.primary"
                >
                  Your Skills Passport Awaits!
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  textAlign="center"
                >
                  To unlock your AI-powered Skills Passport, you must first
                  complete a mock interview to verify your abilities. Please
                  ensure you complete it ethically and without any malpractices.
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.success.light, 0.2), // Standard theme color
                    width: "100%",
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight="bold">
                      Why is it important?
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <CheckCircle color="success" />
                      <Typography variant="body1">
                        Showcase your verified skills to top employers.
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <CheckCircle color="success" />
                      <Typography variant="body1">
                        Get AI-powered insights into your unique strengths.
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <CheckCircle color="success" />
                      <Typography variant="body1">
                        Easily share your profile on LinkedIn and more.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    showNotification(
                      "Redirecting you to the mock interview...",
                      "info",
                    );
                    navigate("/gatep/talent/exam-intro");
                  }}
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: "1.1rem",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Start Your Interview
                </Button>
              </Stack>
            </Card>
          </Container>
        </Layout>
      );
    }

    // Generic fallback for other errors
    return (
      <Layout title="Skills Passport">
        <Container>
          <Paper sx={{ p: 3, textAlign: "center", mt: 4 }}>
            <Typography variant="h5" color="error">
              {error}
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Paper>
        </Container>
      </Layout>
    );
  }

  if (!passportData) {
    return (
      <Layout title="Skills Passport">
        <Container>
          <Typography>No passport data available.</Typography>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Skills Passport">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            flex: "1 1 auto",
            overflowX: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            bgcolor: "background.default",
            p: { xs: 1, sm: 2, md: 3 },
            minHeight: 0, // Important for flex scrolling
          }}
        >
          <Box
            sx={{
              transform: `scale(${scale})`,
              transformOrigin: "center top",
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <Box
              ref={passportRef}
              sx={{
                width: "794px",
                height: "1123px",
                bgcolor: "background.paper",
                borderRadius: "24px",
                boxShadow: (theme) =>
                  `0 32px 80px ${alpha(theme.palette.common.black, 0.18)}`,
                p: 4,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              {/* Header */}
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  color: "text.primary",
                  borderRadius: "24px",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  p: 3,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  position: "relative",
                  overflow: "hidden",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "150px",
                    height: "150px",
                    background: (theme) =>
                      `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    transform: "translate(30%, -30%)",
                  },
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <Avatar
                      src={passportData.personalInfo.photo}
                      alt={passportData.personalInfo.name}
                      sx={{
                        width: 100,
                        height: 100,
                        border: (theme) =>
                          `3px solid ${theme.palette.background.paper}`,
                      }}
                      imgProps={{
                        onLoad: handleImageLoad,
                        crossOrigin: "anonymous",
                        onError: () => setAllImagesLoaded(true), // Handle error case
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {passportData.personalInfo.name}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {passportData.personalInfo.title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <LocationOn />
                      <Typography>
                        {passportData.personalInfo.location}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  minHeight: 0,
                }}
              >
                {/* Left column */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "24px",
                      textAlign: "center",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      boxShadow: "none",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      mb={2}
                      fontWeight="bold"
                    >
                      Global Readiness
                    </Typography>

                    <RadialProgress
                      value={passportData.globalReadiness.overallScore}
                    />
                    <Box mt={2}>
                      <Typography variant="body2" mb={0.5}>
                        Relocation:{" "}
                        {passportData.globalReadiness.relocationScore}%
                      </Typography>
                      <Typography variant="body2" mb={0.5}>
                        Cultural Adaptability:{" "}
                        {passportData.globalReadiness.culturalAdaptability}%
                      </Typography>
                      <Typography variant="body2" mb={0.5}>
                        Communication:{" "}
                        {passportData.globalReadiness.communicationSkills}%
                      </Typography>
                      <Typography variant="body2">
                        Technical:{" "}
                        {passportData.globalReadiness.technicalReadiness}%
                      </Typography>
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "24px",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      mb={2}
                      fontWeight="bold"
                    >
                      AI Specializations
                    </Typography>
                    {passportData.aiSpecializations.map((spec, index) => (
                      <Box key={index} mb={2}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={0.5}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {spec.name}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {spec.level}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={spec.level}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.05),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "24px",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                      flex: 1,
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      mb={2}
                      fontWeight="bold"
                    >
                      Frameworks & Tools
                    </Typography>

                    <Box>
                      {passportData.frameworks.length > 0 ? (
                        <Grid container spacing={1}>
                          {passportData.frameworks.map((fw, i) => (
                            <Grid item xs={12} key={i}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderRadius: "16px",
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.02),
                                  border: (theme) =>
                                    `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {fw.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {fw.projects} projects
                                </Typography>
                                <Box mt={0.5}>
                                  <Rating
                                    size="small"
                                    value={fw.proficiency}
                                    readOnly
                                    precision={0.5}
                                  />
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No frameworks or tools listed.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>

                {/* Right column */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "24px",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      mb={2}
                      fontWeight="bold"
                    >
                      AI-Generated Insights
                    </Typography>
                    <Typography
                      variant="body2"
                      paragraph
                      color="text.secondary"
                      sx={{
                        fontStyle: "italic",
                        my: 1,
                        lineHeight: 1.6,
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.03),
                        p: 2,
                        borderRadius: "16px",
                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                      }}
                    >
                      "{passportData.aiGeneratedInsights.summary}"
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ mt: 2, mb: 1, color: "primary.main" }}
                    >
                      Key Strengths
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {passportData.aiGeneratedInsights.strengths.map(
                        (strength, i) => (
                          <Chip
                            key={i}
                            label={strength}
                            variant="outlined"
                            sx={{
                              borderColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.3),
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.05),
                              color: "primary.main",
                              fontWeight: "bold",
                              borderRadius: "8px",
                            }}
                            size="small"
                          />
                        ),
                      )}
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "24px",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.02),
                      flex: 1,
                      border: (theme) =>
                        `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      mb={2}
                      fontWeight="bold"
                    >
                      Verified Certifications
                    </Typography>

                    <Box>
                      {passportData.certifications.length > 0 ? (
                        passportData.certifications.map((cert, i) => (
                          <Paper
                            key={i}
                            elevation={0}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              p: 1.5,
                              borderRadius: "16px",
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.02),
                              border: (theme) =>
                                `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                              mb:
                                i < passportData.certifications.length - 1
                                  ? 1.5
                                  : 0,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {cert.name}
                              </Typography>
                              <Typography variant="caption" color="primary">
                                by {cert.issuer}
                              </Typography>
                            </Box>
                          </Paper>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No verified certifications listed.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper
          square
          elevation={0}
          sx={{
            flexShrink: 0,
            py: 2.5,
            px: 3,
            textAlign: "center",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            borderTop: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<Image />}
              onClick={() => handleDownload("jpeg")}
              disabled={isGenerating || !allImagesLoaded}
              sx={{ minWidth: 140 }}
            >
              {isGenerating ? "Generating..." : "Download JPEG"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={() => handleDownload("pdf")}
              disabled={isGenerating || !allImagesLoaded}
              sx={{ minWidth: 140 }}
            >
              {isGenerating ? "Generating..." : "Download PDF"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.print()}
              sx={{ minWidth: 100 }}
            >
              Print
            </Button>
          </Box>
          {!allImagesLoaded && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={1}
            >
              Loading images... Please wait before downloading.
            </Typography>
          )}
        </Paper>
      </Box>
    </Layout>
  );
};

export default SkillsPassport;
